<template>
    <div class="scrollable-container">
        <svg class="canvas-svg" xmlns="http://www.w3.org/2000/svg" :width="canvasWidth" :height="canvasHeight">

            <!-- 矩形：画布 -->
            <g v-for="pageIndex in totalPages" :key="'page-' + pageIndex">
                <rect v-if="!isExporting" :x="rectX" :y="rectY + getPageOffset(pageIndex - 1)" :width="rectWidth"
                    :height="rectHeight" fill="#fff" stroke="#7b7b7b" stroke-width="1"></rect>
            </g>

            <!-- 渲染 SVG 元素 -->
            <g v-for="element in svgList" :key="element.id">

                <!-- 渲染标题 -->
                <text v-if="element.id === 'title'" :x="element.attrs.x" :y="renderSettings.margin.top + 50"
                    text-anchor="middle" :font-size="renderSettings.fontSize.title" :fill="element.attrs.fill"
                    :font-family="element.attrs.fontFamily" :font-weight="element.attrs.fontWeight"
                    :font-style="element.attrs.fontStyle" dominant-baseline="middle">
                    {{ element.attrs.text }}
                </text>

                <!-- 渲染 Metadata -->
                <text v-if="element.id === 'key'" :x="barXStart + 60" :y="barYBase(1) + rollBarWidth"
                    :font-size="renderSettings.fontSize.fingerings" :fill="element.attrs.fill"
                    :font-family="element.attrs.fontFamily" :font-weight="element.attrs.fontWeight"
                    :font-style="element.attrs.fontStyle">
                    {{ element.attrs.text }}
                </text>

                <!-- 渲染 作者 -->
                <text v-if="element.id === 'author'" :x="barXStart + 60 + barWidth" :y="barYBase(1) + rollBarWidth"
                    text-anchor="end" :font-size="renderSettings.fontSize.fingerings" :fill="element.attrs.fill"
                    :font-family="element.attrs.fontFamily" :font-weight="element.attrs.fontWeight"
                    :font-style="element.attrs.fontStyle" dominant-baseline="middle">
                    {{ element.attrs.text }}
                </text>
            </g>

            <!-- 卷帘谱 -->
            <!-- 渲染 BAR 元素 -->
            <g v-if="currentMode === 'piano-roll'">
                <g v-for="groupIndex in barCount" :key="'bar-group-' + groupIndex">

                    <!-- 列标题 -->
                    <g>
                        <text :x="barXStart" :y="barYBase(groupIndex) + rollBarWidth * 1.9"
                            :font-size="rollBarWidth * 0.5" font-weight="bold" fill="#000" text-anchor="middle">
                            {{ $t('tablature.key') }}
                        </text>
                        <text :x="barXStart + 40" :y="barYBase(groupIndex) + rollBarWidth * 1.9"
                            :font-size="rollBarWidth * 0.5" font-weight="bold" fill="#000" text-anchor="middle">
                            {{ $t('tablature.stringNo') }}
                        </text>

                        <!-- 每行文字 -->
                        <g v-for="(note, noteIndex) in 7" :key="'note-' + groupIndex + '-' + noteIndex">
                            <text :x="barXStart"
                                :y="rectYBase(groupIndex) + noteIndex * rollBarWidth + rollBarWidth * 0.5"
                                :font-size="rollBarWidth * 0.5" fill="#000" text-anchor="middle"
                                alignment-baseline="central">
                                {{ tuningNotes[noteIndex] }}
                            </text>
                            <text :x="barXStart + 40"
                                :y="rectYBase(groupIndex) + noteIndex * rollBarWidth + rollBarWidth * 0.5"
                                :font-size="rollBarWidth * 0.5" fill="#000" text-anchor="middle"
                                alignment-baseline="central">
                                {{ noteIndex + 1 }}
                            </text>
                        </g>
                    </g>

                    <!-- 长线 -->
                    <line v-for="lineIndex in (manualLayout ? manualBarBeats(groupIndex) : beatNum) + 1"
                        :key="'long-line-' + groupIndex + '-' + lineIndex"
                        :x1="rectXStart + (lineIndex - 1) * barInterval(groupIndex)"
                        :x2="rectXStart + (lineIndex - 1) * barInterval(groupIndex)" :y1="getLineStartY(groupIndex)"
                        :y2="getLineEndY(groupIndex)" stroke="#000" stroke-width="1" />

                    <!-- 短线 -->
                    <line v-for="lineIndex in (manualLayout ? manualBarBeats(groupIndex) : beatNum)"
                        :key="'short-line-' + groupIndex + '-' + lineIndex"
                        :x1="rectXStart + lineIndex * barInterval(groupIndex) - barInterval(groupIndex) / 2"
                        :x2="rectXStart + lineIndex * barInterval(groupIndex) - barInterval(groupIndex) / 2"
                        :y1="getLineStartY(groupIndex)" :y2="barYBase(groupIndex) + rollBarWidth * 2" stroke="#000"
                        stroke-width="1" />

                    <!-- 矩形 -->
                    <g v-for="(line, lineIndex) in 7" :key="'line-' + groupIndex + '-' + lineIndex">
                        <!-- 背景色 -->
                        <rect :x="rectXStart" :y="rectYBase(groupIndex) + lineIndex * rollBarWidth" :width="barWidth"
                            :height="rollBarWidth"
                            :fill="lineIndex % 2 === 0 ? settings.rollBarColor2 : settings.rollBarColor1"
                            stroke="none" />
                    </g>
                    <!-- 左竖线 -->
                    <line :x1="rectXStart" :y1="rectYBase(groupIndex)" :x2="rectXStart"
                        :y2="rectYBase(groupIndex) + 7 * rollBarWidth" stroke="#000" stroke-width="1" />

                    <!-- 右竖线 -->
                    <line :x1="rectXStart + barWidth" :y1="rectYBase(groupIndex)" :x2="rectXStart + barWidth"
                        :y2="rectYBase(groupIndex) + 7 * rollBarWidth" stroke="#000" stroke-width="1" />

                    <!-- 顶部横线 -->
                    <line :x1="rectXStart" :y1="rectYBase(groupIndex)" :x2="rectXStart + barWidth"
                        :y2="rectYBase(groupIndex)" stroke="#000" stroke-width="1" />

                    <!-- 底部横线 -->
                    <line :x1="rectXStart" :y1="rectYBase(groupIndex) + 7 * rollBarWidth" :x2="rectXStart + barWidth"
                        :y2="rectYBase(groupIndex) + 7 * rollBarWidth" stroke="#000" stroke-width="1" />
                </g>


                <!-- 渲染音符 -->
                <g v-for="(note, noteIndex) in noteList" :key="'note-' + noteIndex">
                    <g v-if="!note.stop">
                        <rect :x="rectXStart + note.beatIndex * barInterval(note.barIndex)"
                            :y="rectYBase(note.barIndex) + (note.stringNo - 1) * rollBarWidth"
                            :width="barInterval(note.barIndex) * note.duration" :height="rollBarWidth"
                            :fill="settings.noteFillColor" :stroke="settings.noteStrokeColor" stroke-width="2" 
                            :rx="settings.noteCornerStyle === 'rounded' ? 8 : 0"
                            :ry="settings.noteCornerStyle === 'rounded' ? 8 : 0"  />

                        <!-- 右手三角 -->
                        <polygon v-if="note.rightHandDirection" :points="getTrianglePoints(note)"
                            :fill="settings.triangleFillColor" :stroke="settings.triangleStrokeColor" stroke-width="1.5"
                            shape-rendering="crispEdges" />
                        <!-- 特殊：打下 -->
                        <text v-if="note.specialRightHand"
                            :x="rectXStart + note.beatIndex * barInterval(note.barIndex) + barInterval(note.barIndex) * note.duration - barInterval(note.barIndex) * 0.1"
                            :y="rectYBase(note.barIndex) + (note.stringNo - 1) * rollBarWidth + rollBarWidth / 2"
                            text-anchor="middle" :font-size="0.5 * rollBarWidth" alignment-baseline="central"
                            fill="#000" font-weight="bold">
                            打
                        </text>

                        <!-- 徽位 -->
                        <text :x="getLeftHandTextX(note)"
                            :y="rectYBase(note.barIndex) + (note.stringNo - 1) * rollBarWidth + rollBarWidth / 2"
                            :font-size="getFontSize(settings.positionSize, 'roll') * rollBarWidth" 
                            text-anchor="left" alignment-baseline="central"
                            :fill="settings.positionColor" :font-family="settings.positionFont"
                            :font-weight="settings.piaonHuiBold ? 'bold' : 'normal'" :font-style="settings.piaonHuiItalic ? 'italic' : 'normal'">
                            {{ note.huiNo }}
                        </text>

                        <!-- 左手svg -->
                        <!-- 位于当前音里面的 -->
                        <use v-if="note.leftHandSvg && note.leftHandSvgPos === 'in'"
                            :xlink:href="`#${note.leftHandSvg}`"
                            :x="rectXStart + note.beatIndex * barInterval(note.barIndex) + 0.01 * barInterval(note.barIndex)"
                            :y="rectYBase(note.barIndex) + (note.stringNo - 0.9) * rollBarWidth"
                            :width="rollBarWidth * 0.4" :height="rollBarWidth * 0.8"
                            preserveAspectRatio="xMidYMid meet" />

                        <!-- 位于当前音上面的 -->
                        <use v-if="note.leftHandSvg && note.leftHandSvgPos === 'up'"
                            :xlink:href="`#${note.leftHandSvg}`"
                            :x="rectXStart + note.beatIndex * barInterval(note.barIndex) + 0.02 * barInterval(note.barIndex)"
                            :y="rectYBase(note.barIndex) + (note.stringNo - 1.35) * rollBarWidth"
                            :width="rollBarWidth * 0.5" :height="rollBarWidth * 0.25"
                            preserveAspectRatio="xMidYMid meet" />

                        <!-- 指法标注 -->
                        <g v-if="showFingerings">
                            <text v-if="note.leftHandFinger"
                                :x="rectXStart + note.beatIndex * barInterval(note.barIndex) + barInterval(note.barIndex) * note.duration * 0.1"
                                :y="rectYBase(note.barIndex) + 7 * rollBarWidth + fingeringsBottom"
                                :font-size="0.4 * rollBarWidth" text-anchor="middle" alignment-baseline="hanging"
                                :fill="settings.fingersColor" :font-family="settings.fingersFont"
                                :font-weight="settings.fingersBold ? 'bold' : 'normal'"
                                :font-style="settings.fingersItalic ? 'italic' : 'normal'">
                                {{ note.leftHandFinger }}
                            </text>
                            <text v-if="note.rightHandFinger"
                                :x="rectXStart + note.beatIndex * barInterval(note.barIndex) + barInterval(note.barIndex) * note.duration * 0.9"
                                :y="rectYBase(note.barIndex) + 7 * rollBarWidth + fingeringsBottom"
                                :font-size="0.4 * rollBarWidth" text-anchor="middle" alignment-baseline="hanging"
                                :fill="settings.fingersColor" :font-family="settings.fingersFont"
                                :font-weight="settings.fingersBold ? 'bold' : 'normal'"
                                :font-style="settings.fingersItalic ? 'italic' : 'normal'">
                                {{ note.rightHandFinger }}
                            </text>
                        </g>
                    </g>

                    <!-- 歌词 -->
                    <text v-if="note.lyric"
                        :x="getLyricX(note, settings.lyricsAlign)"
                        :y="showFingerings ? rectYBase(note.barIndex) + 7 * rollBarWidth + fingeringsBottom + songBottom : rectYBase(note.barIndex) + 7 * rollBarWidth + songBottom"
                        :font-size="renderSettings.fontSize.lyrics" :text-anchor="getTextAnchor(settings.lyricsAlign)" alignment-baseline="hanging"
                        :fill="settings.lyricsColor" :font-weight="settings.lyricsBold ? 'bold' : 'normal'"
                        :font-family="settings.lyricsFont" :font-style="settings.lyricsItalic ? 'italic' : 'normal'">
                        {{ note.lyric }}
                    </text>
                </g>

                <!-- 渲染小节线 -->
                <g v-for="(line, lineIndex) in lineList" :key="'line-' + lineIndex">
                    <line :x1="rectXStart + line.beatIndex * barInterval(line.barIndex)"
                        :x2="rectXStart + line.beatIndex * barInterval(line.barIndex)" :y1="rectYBase(line.barIndex)"
                        :y2="rectYBase(line.barIndex) + rollBarWidth * 7" stroke="#000" stroke-width="0.5" />
                </g>
            </g>



            <!-- 七线谱 -->
            <!-- 渲染 LINE 元素 -->
            <g v-else-if="currentMode === 'guitar-tablature'">
                <g v-for="groupIndex in barCount" :key="'bar-group-' + groupIndex">

                    <!-- 列标题 -->
                    <g>
                        <text :x="barXStart" :y="lineYBase(groupIndex)" :font-size="stringSpacing * 0.8"
                            font-weight="bold" fill="#000" text-anchor="middle">
                            {{ $t('tablature.key') }}
                        </text>
                        <text :x="barXStart + 40" :y="lineYBase(groupIndex)" :font-size="stringSpacing * 0.8"
                            font-weight="bold" fill="#000" text-anchor="middle">
                            {{ $t('tablature.stringNo') }}
                        </text>

                        <!-- 每行文字 -->
                        <g v-for="(note, noteIndex) in 7" :key="'note-' + groupIndex + '-' + noteIndex">
                            <text :x="barXStart" :y="lineYBase(groupIndex) + noteIndex * stringSpacing + stringSpacing"
                                :font-size="stringSpacing * 0.8" fill="#000" text-anchor="middle"
                                alignment-baseline="central">
                                {{ tuningNotes[noteIndex] }}
                            </text>
                            <text :x="barXStart + 40"
                                :y="lineYBase(groupIndex) + noteIndex * stringSpacing + stringSpacing"
                                :font-size="stringSpacing * 0.8" fill="#000" text-anchor="middle"
                                alignment-baseline="central">
                                {{ noteIndex + 1 }}
                            </text>
                        </g>
                    </g>

                    <!-- 七根线 -->
                    <line v-for="lineIndex in 7" :key="'seven-string-line-' + groupIndex + '-' + lineIndex"
                        :x1="rectXStart" :x2="rectXStart + barWidth"
                        :y1="lineYBase(groupIndex) + lineIndex * stringSpacing"
                        :y2="lineYBase(groupIndex) + lineIndex * stringSpacing"
                        :stroke="lineIndex === 4 ? settings.lineColor : settings.lineBoldColor"
                        :stroke-width="lineIndex === 4 ? 2 : 1" />
                </g>

                <!-- 渲染符尾竖线 -->
                <g v-for="(note, noteIndex) in notePositions" :key="'stem-' + noteIndex">
                    <line :x1="getStemX(note)" :y1="getStemStartY(note)" :x2="getStemX(note)" :y2="getStemEndY(note)"
                        stroke="#000" stroke-width="1" />
                </g>

                <!-- 渲染音符 -->
                <g v-for="(note, noteIndex) in notePositions" :key="'note-' + noteIndex">

                    <!-- 符头背景 -->
                    <rect v-if="!note.stop" :x="getBackgroundX(note)"
                        :y="lineYBase(note.barIndex) + note.stringNo * stringSpacing - stringSpacing / 2"
                        :width="getBackgroundWidth(note)" :height="stringSpacing" fill="#fff" stroke="none" />

                    <!-- 徽位数字 -->
                    <text v-if="!note.stop" :x="getTextX(note)"
                        :y="lineYBase(note.barIndex) + note.stringNo * stringSpacing" 
                        :font-size="getFontSize(settings.linePositionSize, 'line') * stringSpacing"
                        text-anchor="middle" alignment-baseline="central" :fill="settings.linePositionColor"
                        :font-family="settings.linePositionFont" :font-weight="settings.lineHuiBold ? 'bold' : 'normal'"
                        :font-style="settings.lineHuiItalic ? 'italic' : 'normal'">
                        {{ note.huiNo }}
                    </text>

                    <!-- 右手三角形 -->
                    <polygon v-if="note.rightHandDirection && !note.stop" :points="getTrianglePointsGuitar(note)"
                        :fill="settings.lineTriangleFillColor" :stroke="settings.lineTriangleStrokeColor"
                        stroke-width="1.5" />

                    <!-- 八分音符及以下时值的翘起线条 -->
                    <g v-if="needFlags(note, noteIndex)">
                        <line v-for="(_, index) in getFlags(note)" :key="'flag-' + index"
                            :x1="note.centerX + stringSpacing * 0.4"
                            :y1="note.stemY - stringSpacing * 0.3 * (index + 1)" :x2="note.centerX"
                            :y2="index === 0 ? note.stemY : note.stemY - stringSpacing * 0.3 * index" stroke="#000"
                            stroke-width="1.5" />
                    </g>

                    <!-- 增时线 -->
                    <g v-if="needExtensionLines(note)">
                        <line v-for="(_, index) in getExtensionLineCount(note)" :key="'extension-' + index"
                            :x1="note.centerX + stringSpacing * 0.4 + index * stringSpacing * 0.5"
                            :y1="note.stemY - stringSpacing * 0.8"
                            :x2="note.centerX + stringSpacing * 0.8 + index * stringSpacing * 0.5"
                            :y2="note.stemY - stringSpacing * 0.8" stroke="#000" stroke-width="1" />
                    </g>

                    <!-- 附点 -->
                    <circle v-if="hasDot(note)" :cx="note.centerX + stringSpacing * 0.3"
                        :cy="note.stemY - stringSpacing * 0.8" :r="stringSpacing * 0.07" fill="#000" />

                    <!-- 指法标注 -->
                    <g v-if="showFingerings && !note.stop">
                        <text v-if="note.leftHandFinger" :x="note.centerX - noteWidth(note.barIndex) * 0.15"
                            :y="note.stemY + fingeringsBottom" :font-size="stringSpacing * 0.7" text-anchor="middle"
                            alignment-baseline="hanging" :fill="settings.fingersColor"
                            :font-family="settings.fingersFont" :font-weight="settings.fingersBold ? 'bold' : 'normal'"
                            :font-style="settings.fingersItalic ? 'italic' : 'normal'">
                            {{ note.leftHandFinger }}
                        </text>
                        <text v-if="note.rightHandFinger" :x="note.centerX + noteWidth(note.barIndex) * 0.15"
                            :y="note.stemY + fingeringsBottom" :font-size="stringSpacing * 0.7" text-anchor="middle"
                            alignment-baseline="hanging" :fill="settings.fingersColor"
                            :font-family="settings.fingersFont" :font-weight="settings.fingersBold ? 'bold' : 'normal'"
                            :font-style="settings.fingersItalic ? 'italic' : 'normal'">
                            {{ note.rightHandFinger }}
                        </text>
                    </g>

                    <!-- 歌词 -->
                    <text v-if="note.lyric" :x="note.centerX"
                        :y="showFingerings ? note.stemY + fingeringsBottom + songBottom : note.stemY + songBottom"
                        :font-size="renderSettings.fontSize.lyrics" text-anchor="middle" alignment-baseline="hanging"
                        :fill="settings.lyricsColor" :font-weight="settings.lyricsBold ? 'bold' : 'normal'"
                        :font-family="settings.lyricsFont" :font-style="settings.lyricsItalic ? 'italic' : 'normal'">
                        {{ note.lyric }}
                    </text>
                </g>

                <!-- 渲染连音线 -->
                <g v-for="(note, noteIndex) in notePositions" :key="'tie-' + noteIndex">
                    <path v-if="needTie(note, noteIndex) && !note.stop" :d="getTiePath(note, noteIndex)" fill="none"
                        stroke="#000" stroke-width="1.5" />
                </g>

                <!-- 渲染符尾组合线 -->
                <g v-for="(group, groupIndex) in findNoteGroups()" :key="'beam-group-' + groupIndex">
                    <!-- 第一条线总是完整连接 -->
                    <line :x1="group[0].note.centerX" :y1="group[0].note.stemY"
                        :x2="group[group.length - 1].note.centerX" :y2="group[group.length - 1].note.stemY"
                        stroke="#000" stroke-width="1.5" />

                    <!-- 后续的线 -->
                    <g v-for="(_, lineIndex) in getBeamCount(group).slice(1)" :key="'beam-' + lineIndex">
                        <g v-for="(note, noteIndex) in group" :key="'beam-segment-' + noteIndex">
                            <line v-if="needSecondaryBeam(note.note, lineIndex, noteIndex, group)" :x1="noteIndex === group.length - 1 ?
                                note.note.centerX - noteWidth(note.note.barIndex) * 0.2 :
                                note.note.centerX" :y1="note.note.stemY - stringSpacing * 0.2 * (lineIndex + 1)" :x2="noteIndex < group.length - 1 && note.note.duration === group[noteIndex + 1].note.duration ?
                                    group[noteIndex + 1].note.centerX :
                                    noteIndex === group.length - 1 ?
                                        note.note.centerX :
                                        note.note.centerX + noteWidth(note.note.barIndex) * 0.2"
                                :y2="note.note.stemY - stringSpacing * 0.2 * (lineIndex + 1)" stroke="#000"
                                stroke-width="1.5" />
                        </g>
                    </g>
                </g>

                <!-- 渲染特殊指法 -->
                <g v-for="(note, noteIndex) in notePositions" :key="'special-' + noteIndex">
                    <g v-if="!note.stop">
                        <use v-if="note.leftHandSvg && note.leftHandSvgPos === 'up'"
                            :xlink:href="`#${note.leftHandSvg}`" :x="note.centerX - stringSpacing * 0.25"
                            :y="lineYBase(note.barIndex)" :width="stringSpacing * 0.8" :height="stringSpacing * 0.5"
                            preserveAspectRatio="xMidYMid meet" />

                        <text v-if="note.specialRightHand" :x="note.centerX"
                            :y="lineYBase(note.barIndex) + stringSpacing * 0.8" text-anchor="middle" fill="#000"
                            :font-size="stringSpacing * 0.7">
                            {{ note.specialRightHand }}
                        </text>

                        <use v-if="note.leftHandSvg && note.leftHandSvgPos === 'in' && note.leftHandSvg !== 'arrowup'"
                            :xlink:href="`#${note.leftHandSvg}`" :x="getBackgroundX(note) - stringSpacing"
                            :y="lineYBase(note.barIndex) + (note.stringNo - 1) * stringSpacing"
                            :width="stringSpacing * 1.0" :height="stringSpacing * 1.5"
                            preserveAspectRatio="xMidYMid meet" />
                    </g>
                </g>

                <!-- 渲染小节线 -->
                <g v-for="(line, lineIndex) in lineList" :key="'line-' + lineIndex">
                    <line :x1="rectXStart + (line.beatIndex - 0.5) * noteWidth(line.barIndex)"
                        :x2="rectXStart + (line.beatIndex - 0.5) * noteWidth(line.barIndex)"
                        :y1="lineYBase(line.barIndex) + stringSpacing"
                        :y2="lineYBase(line.barIndex) + stringSpacing * 7" stroke="#666" stroke-width="0.9" />
                </g>
            </g>

        </svg>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import jsPDF from "jspdf";
// import { Canvg } from "canvg";


export default {
    name: "TablatureBar",
    data() {
        return {
            containerWidth: 0,
            containerHeight: 0,
            // tuningNotes: ["C", "D", "F", "G", "A", "c", "d"], // 左侧定调文字
            isExporting: false
        };
    },
    computed: {
        ...mapGetters(["settings", "svgList", "noteList", "lineList", "currentMode", "barCount", "showFingerings", "manualLayout", "tuningNotes"]),

        canvasWidth() {
            return this.containerWidth || 800; // 默认宽度
        },
        canvasHeight() {
            return this.rectHeight * this.totalPages + this.rectY * 2 || 600; // 默认高度
        },

        totalPages() {
            const lastBarPage = this.barPageMapping.get(this.barCount) || 0;
            return lastBarPage + 1; // 加1是因为页码从0开始
        },

        rectWidth() {
            return this.canvasWidth * 0.9; // 动态宽度占 90%
        },
        rectHeight() {
            const aspectRatio = this.settings.paperSize === "A4" ? 3508 / 2480 : 792 / 612;
            return this.rectWidth * aspectRatio;
        },

        // 当前与标准尺寸的比例
        scale() {
            return this.rectWidth / 2480;
        },

        rectX() {
            return (this.canvasWidth - this.rectWidth) / 2; // 水平居中
        },
        rectY() {
            return 20; // 顶部固定偏移
        },

        songtop() {
            return this.renderSettings.spacing.songTop;
        },

        rollBarWidth() {
            return this.renderSettings.rollBarWidth;
        },
        stringSpacing() {
            return this.renderSettings.stringSpacing;
        },

        fingeringsBottom() {
            return this.renderSettings.spacing.fingeringsBottom;
        },

        songBottom() {
            return this.renderSettings.spacing.songBottom;
        },

        beatNum() {
            return this.settings.beatNum;
        },
        barWidth() {
            return this.containerWidth * 0.9 - this.renderSettings.margin.left - this.renderSettings.margin.right - 60;
        },
        barXStart() {
            return this.renderSettings.margin.left + this.containerWidth * 0.05;
        },
        rectXStart() {
            return this.renderSettings.margin.left + this.containerWidth * 0.05 + 60;
        },

        // 修改小节高度计算
        barHeight() {
            return this.currentMode === 'piano-roll'
                ? (this.rollBarWidth * 7.5 + this.renderSettings.spacing.songTop +
                    (this.showFingerings ? this.renderSettings.spacing.fingeringsBottom : 0) +
                    this.songBottom)
                : (this.stringSpacing * 7.5 + this.renderSettings.spacing.songTop * 2 +
                    (this.showFingerings ? this.renderSettings.spacing.fingeringsBottom : 0) +
                    this.songBottom * 3);
        },

        // 页面可用高度计算
        pageUsableHeight() {
            return (pageIndex) => {
                const baseHeight = this.rectHeight - this.renderSettings.margin.top - this.renderSettings.margin.bottom - 80;
                // 只在第一页减去一个 songTop
                return pageIndex === 0 ?
                    baseHeight - this.renderSettings.spacing.songTop:
                    baseHeight;
            };
        },

        // 修改 barPageMapping 以使用新的 pageUsableHeight 方法
        barPageMapping() {
            return this.updateBarPageMapping();
        },


        notePositions() {
            return this.noteList.map(note => ({
                ...note,
                centerX: this.getStemX(note),
                headY: this.lineYBase(note.barIndex) + note.stringNo * this.stringSpacing - this.stringSpacing / 2,
                stemY: this.getStemEndY(note)
            }));
        },

        noteGroups() {
            return this.findNoteGroups();
        },

        // 追踪哪些音符在组中
        groupedNoteIndices() {
            return new Set(
                this.noteGroups.flatMap(group => group.map(item => item.index))
            );
        },

        renderSettings() {
            return {
                margin: {
                    top: this.settings.margin.top * this.scale,
                    bottom: this.settings.margin.bottom * this.scale,
                    left: this.settings.margin.left * this.scale,
                    right: this.settings.margin.right * this.scale
                },

                // 字体大小
                fontSize: {
                    title: this.settings.fontSize.title * this.scale,
                    fingerings: this.settings.fontSize.fingerings * this.scale,
                    lyrics: this.settings.fontSize.lyrics * this.scale
                },

                // 间距设置
                spacing: {
                    songBottom: this.settings.spacing.songBottom * this.scale,
                    fingeringsBottom: this.settings.spacing.fingeringsBottom * this.scale,
                    songTop: this.settings.spacing.songTop * this.scale
                },

                // 琴卷设置
                rollBarWidth: this.settings.rollBarWidth * this.scale,
                stringSpacing: this.settings.stringSpacing * this.scale,
            };
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.updateContainerDimensions();
            window.addEventListener("resize", this.updateContainerDimensions);
        });
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.updateContainerDimensions);
    },
    methods: {
        getFontSize(size, mode) {
            const sizeMapRoll = {
                'small': 0.5,
                'medium': 0.6,
                'large': 0.7
            };
            
            const sizeMapLine = {
                'small': 0.9,
                'medium': 1.0,
                'large': 1.1
            };

            // 根据模式选择对应的映射
            const sizeMap = mode === 'roll' ? sizeMapRoll : sizeMapLine;
            return sizeMap[size] || (mode === 'roll' ? 0.5 : 0.9); // 根据模式返回默认值
        },

        getLyricX(note, align) {
            const startX = this.rectXStart + note.beatIndex * this.barInterval(note.barIndex);
            const width = this.barInterval(note.barIndex) * note.duration;
            
            switch(align) {
                case 'left':
                    return startX; // 左边界
                case 'right':
                    return startX + width; // 右边界
                default: // center
                    return startX + width * 0.5; // 中点位置
            }
        },

        getTextAnchor(align) {
            const alignMap = {
                'left': 'start',
                'center': 'middle',
                'right': 'end'
            };
            return alignMap[align] || 'middle'; // 默认居中
        },

        // 获取指定页面上的所有小节
        getBarsOnPage(pageIndex) {
            return [...this.barPageMapping.entries()]
                .filter(([, page]) => page === pageIndex)
                .map(([barIndex]) => barIndex);
        },

        // 判断是否是最后一页
        isLastPage(pageIndex) {
            return pageIndex === Math.max(...this.barPageMapping.values());
        },

        // 计算每页小节之间的间距
        getBarSpacing(pageIndex) {
            const barsOnPage = this.getBarsOnPage(pageIndex);
            const barCount = barsOnPage.length;
            if (barCount <= 1) return 0;

            // 计算可用空间
            const usableHeight = this.pageUsableHeight(pageIndex);
            // 所有小节占用的总高度
            const totalBarHeight = barCount * this.barHeight;

            if (this.isLastPage(pageIndex)) {
                // 如果是最后一页，获取前一页的间距
                const previousPageIndex = pageIndex - 1;
                if (previousPageIndex >= 0) {
                    return this.getBarSpacing(previousPageIndex);
                }
            }

            // 计算剩余空间，平均分配给间距
            return (usableHeight - totalBarHeight) / (barCount - 1);
        },

        // 修改获取小节Y坐标的方法
        getBarY(barIndex) {
            if (barIndex < 1 || barIndex > this.barCount) {
                return 0;
            }

            const page = this.barPageMapping.get(barIndex);
            if (page === undefined) {
                console.warn(`No page mapping found for bar ${barIndex}`);
                return 0;
            }

            // 获取当前页面上的所有小节
            const barsOnPage = this.getBarsOnPage(page);
            // 获取当前小节是页面上的第几个（从0开始）
            const indexOnPage = barsOnPage.indexOf(barIndex);

            // 第一页需要额外的 songTop 偏移
            const firstPageOffset = page === 0 ? this.renderSettings.spacing.songTop + this.renderSettings.fontSize.title : 0;

            // 如果是最后一页，使用紧凑布局
            if (this.isLastPage(page)) {
                return this.renderSettings.margin.top +
                    firstPageOffset +
                    page * this.rectHeight +
                    indexOnPage * this.barHeight;
            }

            // 非最后一页使用均匀间距
            const spacing = this.getBarSpacing(page);
            return this.renderSettings.margin.top +
                firstPageOffset +
                page * this.rectHeight +
                indexOnPage * (this.barHeight + spacing);
        },

        // 修改 barPageMapping 的计算逻辑，确保正确分页
        updateBarPageMapping() {
            let currentPage = 0;
            let barsOnCurrentPage = 0;
            const mapping = new Map();

            for (let i = 1; i <= this.barCount; i++) {
                // 计算当前页是否还能放下一个小节
                const currentPageSpace = this.pageUsableHeight(currentPage);
                const minimumSpaceNeeded = (barsOnCurrentPage + 1) * this.barHeight;

                if (minimumSpaceNeeded > currentPageSpace) {
                    // 如果放不下，换到下一页
                    currentPage++;
                    barsOnCurrentPage = 0;
                }

                mapping.set(i, currentPage);
                barsOnCurrentPage++;
            }

            return mapping;
        },

        barYBase(groupIndex) {
            return this.getBarY(groupIndex);
        },

        rectYBase(groupIndex) {
            return this.getBarY(groupIndex) + this.rollBarWidth * 2 + 10;
        },

        lineYBase(groupIndex) {
            return this.getBarY(groupIndex) + 45;
        },

        // 获取线条的起始Y坐标 (下面的点)
        getLineStartY(groupIndex) {
            return this.rectYBase(groupIndex);
        },

        // 获取线条的结束Y坐标 (上面的点)
        getLineEndY(groupIndex) {
            return this.barYBase(groupIndex) + this.rollBarWidth * 1.75;
        },

        manualBarBeats(barIndex) {
            // 先获取当前小节的音符
            const barNotes = this.noteList.filter(note => note.barIndex === barIndex);


            // 对同一个beatIndex只保留第一个
            const uniqueNotes = barNotes.filter((note, index) =>
                barNotes.findIndex(n => n.beatIndex === note.beatIndex) === index
            );

            // 计算总时值
            let maxBeat = 0;
            uniqueNotes.forEach(note => {
                if (this.currentMode === 'piano-roll')
                    maxBeat += note.duration;
                else
                    maxBeat = note.beatIndex + 1;
            });

            return Math.ceil(maxBeat);
        },
        barInterval(barIndex) {
            if (this.manualLayout) {
                const beatCount = this.manualBarBeats(barIndex) || 1;
                return this.barWidth / beatCount;
            }
            return this.barWidth / this.settings.beatNum; // piano-roll 每拍间隔
        },


        updateContainerDimensions() {
            const container = this.$el?.parentNode || document.body;
            this.containerWidth = container.clientWidth;
            this.containerHeight = container.clientHeight;
        },
        getTrianglePoints(note) {
            const baseX = this.rectXStart + note.beatIndex * this.barInterval(note.barIndex) + this.barInterval(note.barIndex) * note.duration; // 矩形右边缘
            const baseY = this.rectYBase(note.barIndex) + (note.stringNo - 1) * this.rollBarWidth; // 矩形顶部

            const triangleHeight = this.rollBarWidth / 1.8;
            const triangleWidth = this.rollBarWidth / 2.2;

            const adjustedX = baseX - this.barInterval(note.barIndex) * 0.1;
            const centerY = baseY + this.rollBarWidth / 2;

            if (note.rightHandDirection === "in") {
                return `${adjustedX - triangleWidth / 2},${centerY - triangleHeight / 2} 
                    ${adjustedX + triangleWidth / 2},${centerY - triangleHeight / 2} 
                    ${adjustedX},${centerY + triangleHeight / 2}`;
            } else if (note.rightHandDirection === "out") {
                return `${adjustedX - triangleWidth / 2},${centerY + triangleHeight / 2} 
                    ${adjustedX + triangleWidth / 2},${centerY + triangleHeight / 2} 
                    ${adjustedX},${centerY - triangleHeight / 2}`;
            }
            return "";
        },

        noteWidth(barIndex) {
            if (this.manualLayout) {
                const beatCount = this.manualBarBeats(barIndex) || 1;
                return this.barWidth / beatCount;
            }
            return this.barWidth / this.settings.noteNum; // guitar-tablature 每拍间隔
        },

        getBackgroundWidth(note) {
            if (note.stop)
                return this.noteWidth(note.barIndex) * 0.5;
            // console.log(note.barIndex, this.manualBarBeats(note.barIndex))
            if (note.huiNo.toString().includes('↑')) {
                if (note.rightHandDirection) {
                    return this.noteWidth(note.barIndex) * 1.2;
                }
                return this.noteWidth(note.barIndex) * 0.9;
            }
            if (note.rightHandDirection) {
                if (note.huiNo.toString().length > 1) {
                    return this.noteWidth(note.barIndex) * 0.8;
                }
                return this.noteWidth(note.barIndex) * 0.6;
            }
            if (note.huiNo.toString().length > 1) {
                return this.noteWidth(note.barIndex) * 0.6;
            }
            return this.noteWidth(note.barIndex) * 0.5;
        },
        getBackgroundX(note) {
            const offset = (this.noteWidth(note.barIndex) - this.getBackgroundWidth(note)) / 2;
            return this.rectXStart + note.beatIndex * this.noteWidth(note.barIndex) + offset;
        },
        getTextX(note) {
            const backgroundX = this.getBackgroundX(note);
            const backgroundWidth = this.getBackgroundWidth(note);
            if (note.stop)
                backgroundX + backgroundWidth * 0.5;
            if (note.rightHandDirection) {
                return backgroundX + backgroundWidth * 0.65;
            }
            return backgroundX + backgroundWidth * 0.5;
        },
        getTrianglePointsGuitar(note) {
            if (note.stop)
                return;
            const backgroundWidth = this.getBackgroundWidth(note);
            const backgroundX = this.getBackgroundX(note);

            // 调整三角形位置和大小
            const baseX = (note.huiNo.toString().length > 1) ? backgroundX + backgroundWidth * 0.15 : backgroundX + backgroundWidth * 0.2;  // 调整三角形在背景中的位置
            const baseY = this.lineYBase(note.barIndex) + note.stringNo * this.stringSpacing;

            const triangleWidth = this.stringSpacing * 0.6;   // 增加三角形宽度
            const triangleHeight = this.stringSpacing * 0.8;  // 增加三角形高度

            if (note.rightHandDirection === "in") {
                return `${baseX - triangleWidth / 2},${baseY - triangleHeight / 2} 
                        ${baseX + triangleWidth / 2},${baseY - triangleHeight / 2} 
                        ${baseX},${baseY + triangleHeight / 2}`;
            } else {
                return `${baseX - triangleWidth / 2},${baseY + triangleHeight / 2} 
                        ${baseX + triangleWidth / 2},${baseY + triangleHeight / 2}
                        ${baseX},${baseY - triangleHeight / 2}`;
            }
        },

        getStemX(note) {
            // 从符头背景的中点开始
            const backgroundX = this.getBackgroundX(note);
            const backgroundWidth = this.getBackgroundWidth(note);
            return backgroundX + backgroundWidth / 2;
        },

        getStemStartY(note) {
            if (note.stop)
                note.stringNo = 8;
            // 从符头背景的底边中点开始
            return this.lineYBase(note.barIndex) +
                (note.stringNo - 1) * this.stringSpacing +
                this.stringSpacing / 2;  // 背景高度的一半
        },

        getStemEndY(note) {
            // 延伸到比最后一根弦再多一点
            return this.lineYBase(note.barIndex) +
                this.stringSpacing * 7 +  // 7根弦
                this.stringSpacing * 1.8; // 额外延伸的长度
        },

        needTie(note, noteIndex) {
            if (note.stop)
                return false;
            if (!note.stop && (!note.rightHandDirection || note.huiNo.toString().includes('↑'))) return false;

            const nextNote = this.notePositions[noteIndex + 1];
            if (!nextNote) return false;

            // console.log(noteIndex, nextNote.beatIndex, this.notePositions)

            // 如果下一个音符有三角形，或者徽位包含↑，不画连音线
            if (!nextNote.stop && (nextNote.rightHandDirection || nextNote.huiNo.toString().includes('↑')
                || nextNote.beatIndex - note.beatIndex > 1 || nextNote.beatIndex <= 1)) return false;

            return true;
        },

        getTiePath(note, noteIndex) {
            const startX = note.centerX;
            const startY = note.headY;

            // 找到连音线结束的音符和整组最高点
            let endNoteIndex = noteIndex + 1;
            let highestY = startY; // 初始化最高点为起始音符的Y

            while (endNoteIndex < this.notePositions.length) {
                const nextNote = this.notePositions[endNoteIndex];
                if (nextNote.rightHandDirection ||
                    nextNote.beatIndex - this.notePositions[endNoteIndex - 1].beatIndex > 1) {
                    break;
                }
                // 更新最高点
                if (nextNote.headY < highestY) {
                    highestY = nextNote.headY;
                }
                endNoteIndex++;
            }

            const endNote = this.notePositions[endNoteIndex - 1];
            const endX = endNote.centerX;
            const endY = endNote.headY;

            // 计算控制点Y坐标（在最高音符上方）
            const controlY = highestY - this.stringSpacing * 0.8;

            // 检查是否跨行
            if (endNote.barIndex !== note.barIndex) {
                // 计算当前行的结束点
                const firstLineEndX = this.rectXStart + this.barWidth;
                const firstLineEndY = highestY - this.stringSpacing * 0.4;

                // 计算下一行的起始点
                const secondLineStartX = this.rectXStart;
                const secondLineStartY = endY - this.stringSpacing * 0.4;

                // 返回两段路径
                const firstLine = `M ${startX} ${startY} Q ${(startX + firstLineEndX) / 2} ${firstLineEndY} ${firstLineEndX} ${firstLineEndY}`;
                const secondLine = `M ${secondLineStartX} ${secondLineStartY} Q ${(secondLineStartX + endX) / 2} ${secondLineStartY} ${endX} ${endY}`;

                return firstLine + ' ' + secondLine;
            } else {
                // 同一行内的普通连音线
                const controlX = (startX + endX) / 2;

                return `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
            }
        },

        needFlags(note, noteIndex) {
            // 如果时值大于等于1拍，不需要旗子
            if (note.duration >= 1) return false;

            // 如果在组合中，不需要旗子
            if (!note.stop && this.groupedNoteIndices.has(noteIndex)) return false;

            // 检查前一个音符是否在同一个beat上
            const nextNote = this.notePositions[noteIndex + 1];
            if (nextNote && nextNote.beatIndex === note.beatIndex) return false;

            return true;
        },

        getFlags(note) {
            const baseValue = Math.pow(2, -Math.ceil(Math.log2(1 / note.duration)));
            if (Math.abs(note.duration / baseValue - 1.5) < 0.001) {
                return new Array(Math.ceil(Math.log2(1 / baseValue)));
            }
            return new Array(Math.floor(Math.log2(1 / note.duration)));
        },

        needExtensionLines(note) {
            return note.duration > 1;
        },

        getExtensionLineCount(note) {
            return new Array(Math.floor(note.duration) - 1);
        },

        hasDot(note) {
            if (Number.isInteger(note.duration)) return false;

            const wholeDuration = Math.floor(note.duration);
            if (wholeDuration > 0) {
                // 对于大于1的情况，检查小数部分是否是0.5
                const fractionalPart = note.duration - wholeDuration;
                if (Math.abs(fractionalPart - 0.5) < 0.001) return true;
            }
            // 对于小于1的情况，检查是否是某个2的幂的1.5倍
            const baseValue = Math.pow(2, -Math.ceil(Math.log2(1 / note.duration)));
            return Math.abs(note.duration / baseValue - 1.5) < 0.001;
        },

        findNoteGroups() {
            let groups = [];
            let currentGroup = [];
            let currentSum = 0;

            for (let i = 0; i < this.notePositions.length; i++) {
                const currentNote = this.notePositions[i];

                if (currentNote.stop)
                    continue;

                // 跳过大于等于1拍的音符
                if (currentNote.duration >= 1) {
                    currentGroup = [];
                    currentSum = 0;
                    continue;
                }

                // 检查下一个音符是否在同一个beat上
                const nextNote = this.notePositions[i + 1];
                if (nextNote && nextNote.beatIndex === currentNote.beatIndex) {
                    i++; // 增加索引以跳过下一个音符
                }

                // 添加当前音符到组
                currentGroup.push({
                    note: currentNote,
                    index: i
                });
                currentSum += currentNote.duration;

                // 检查是否需要结束当前组
                const nextNoteForGroup = this.notePositions[i + 1];
                const hasBarLine = nextNoteForGroup && nextNoteForGroup.beatIndex - currentNote.beatIndex > 1;
                const nextIsLong = nextNoteForGroup && nextNoteForGroup.duration >= 1;
                const crossesBar = nextNoteForGroup && nextNoteForGroup.barIndex !== currentNote.barIndex;

                // 如果遇到任何中断条件，需要结束当前组
                if (!nextNoteForGroup || hasBarLine || nextIsLong || crossesBar) {
                    // 检查当前组是否构成有效的组（0.5拍或1拍）
                    if (currentGroup.length >= 2) {
                        if (Math.abs(currentSum - 1) < 0.001) {
                            groups.push([...currentGroup]);
                        } else if (Math.abs(currentSum - 0.5) < 0.001) {
                            groups.push([...currentGroup]);
                        }
                    }
                    currentGroup = [];
                    currentSum = 0;
                    continue;
                }

                // 检查是否形成了0.5拍的组
                if (Math.abs(currentSum - 0.5) < 0.001 && currentGroup.length >= 2) {
                    // 看看能否继续组成1拍（需要考虑中断条件）
                    if (nextNoteForGroup && !hasBarLine && !nextIsLong && !crossesBar) {
                        // 有机会继续组成1拍，继续循环
                        continue;
                    } else {
                        // 无法继续组成1拍，保存0.5拍的组
                        groups.push([...currentGroup]);
                        currentGroup = [];
                        currentSum = 0;
                    }
                }
                // 检查是否形成了1拍的组
                else if (Math.abs(currentSum - 1) < 0.001 && currentGroup.length >= 2) {
                    groups.push([...currentGroup]);
                    currentGroup = [];
                    currentSum = 0;
                }
                // 如果超过1拍，放弃当前组
                else if (currentSum > 1) {
                    // 检查放弃前的组是否构成0.5拍
                    if (currentGroup.length >= 2 && Math.abs(currentSum - currentNote.duration - 0.5) < 0.001) {
                        groups.push([...currentGroup.slice(0, -1)]);
                    }
                    currentGroup = [{
                        note: currentNote,
                        index: i
                    }];
                    currentSum = currentNote.duration;
                }
            }

            // 处理最后一组
            if (currentGroup.length >= 2) {
                if (Math.abs(currentSum - 0.5) < 0.001 || Math.abs(currentSum - 1) < 0.001) {
                    groups.push([...currentGroup]);
                }
            }

            // console.log('groups', groups);
            return groups;
        },

        // 判断一个音符是否需要参与组合
        canBeGrouped(note) {
            return note.duration < 1;
        },

        // 获取组合线的数量（根据最小时值决定）
        getBeamCount(group) {
            // 获取组内最小时值对应的线条数
            const minDuration = Math.min(...group.map(item => item.note.duration));
            if (minDuration === 0.5) return [1];  // 八分音符
            if (minDuration === 0.25) return [1, 2];  // 十六分音符
            if (minDuration === 0.125) return [1, 2, 3];  // 三十二分音符
            return [];
        },

        needSecondaryBeam(note, lineIndex, noteIndex, group) {
            // 首先检查是否需要这条次要横线
            const beamCount = this.getFlags(note).length;


            if (lineIndex >= beamCount - 1) return false;

            return (
                // 如果和下一个音符时值相同，画长线
                (noteIndex < group.length - 1 && note.duration === group[noteIndex + 1].note.duration) ||
                // 或者需要画短线（不和前一个音符相连，且不是最后一个音符）
                ((!noteIndex || note.duration !== group[noteIndex - 1].note.duration) &&
                    noteIndex < group.length - 1 &&
                    note.duration !== group[noteIndex + 1].note.duration) ||
                // 或者是最后一个音符且不和前一个音符相连
                (noteIndex === group.length - 1 &&
                    (!noteIndex || note.duration !== group[noteIndex - 1].note.duration))
            );
        },

        getLeftHandTextX(note) {
            return note.leftHandSvgPos === 'in' ? this.rectXStart + note.beatIndex * this.barInterval(note.barIndex) + 0.09 * this.barInterval(note.barIndex)
                : this.rectXStart + note.beatIndex * this.barInterval(note.barIndex) + 0.02 * this.barInterval(note.barIndex);
        },

        getPageOffset(pageIndex) {
            return pageIndex * this.rectHeight;
        },

        exportSvg() {
            this.isExporting = true;
            this.$nextTick(() => {
                const svgElement = this.$el.querySelector("svg");

                // 创建一个新的 SVG 元素，设置为 A4 尺寸
                const newSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                newSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
                newSvg.setAttribute("width", 2480);
                newSvg.setAttribute("height", 3508);

                // 复制原始 SVG 的内容
                const content = svgElement.cloneNode(true);

                // 设置正确的视口
                newSvg.setAttribute("viewBox", `0 0 2480 3508`);

                // 清理 Vue 相关属性
                const cleanElement = (element) => {
                    const attributes = [...element.attributes];
                    attributes.forEach(attr => {
                        if (attr.name.startsWith('v-') ||
                            attr.name.startsWith(':') ||
                            attr.name.startsWith('@') ||
                            attr.name === 'data-v-') {
                            element.removeAttribute(attr.name);
                        }
                    });

                    // 递归清理子元素
                    Array.from(element.children).forEach(cleanElement);
                };

                cleanElement(content);
                newSvg.appendChild(content);

                // 序列化新的 SVG
                const serializer = new XMLSerializer();
                let svgString = serializer.serializeToString(newSvg);

                // 添加 XML 声明
                svgString = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n' + svgString;

                // 创建 Blob 并下载
                const blob = new Blob([svgString], { type: "image/svg+xml" });
                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = "tablature.svg";
                link.click();

                URL.revokeObjectURL(url);
                this.isExporting = false;
            });
        },
        async exportPdf() {
            this.isExporting = true;

            try {
                // 创建 PDF
                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'pt',
                    format: 'a4'
                });

                // A4 尺寸（pt）
                const A4_WIDTH_PT = 595.28;
                const A4_HEIGHT_PT = 841.89;

                // 处理每一页
                for (let pageIndex = 0; pageIndex < this.totalPages; pageIndex++) {
                    if (pageIndex > 0) {
                        pdf.addPage();
                    }

                    try {
                        const svgElement = this.$el.querySelector("svg");
                        if (!svgElement) {
                            throw new Error("SVG element not found");
                        }

                        // 创建高分辨率 canvas
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        // 大幅提高分辨率 - 使用 300dpi 的分辨率
                        const scale = 300 / 72; // 300dpi / 72dpi(默认)
                        canvas.width = A4_WIDTH_PT * scale;
                        canvas.height = A4_HEIGHT_PT * scale;

                        // 设置 canvas 上下文以处理高分辨率
                        ctx.scale(scale, scale);

                        // 设置白色背景
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, canvas.width / scale, canvas.height / scale);

                        // 准备当前页的视口
                        const viewBox = {
                            x: this.rectX,
                            y: this.rectY + this.getPageOffset(pageIndex),
                            width: this.rectWidth,
                            height: this.rectHeight
                        };

                        // 创建字体定义的 CSS
                        const fontFaces = `
                            @font-face {
                                font-family: 'hei';
                                src: url('${require('@/assets/fonts/hei.ttf')}') format('truetype');
                            }
                            @font-face {
                                font-family: 'kai';
                                src: url('${require('@/assets/fonts/kai.ttf')}') format('truetype');
                            }
                            @font-face {
                                font-family: 'song';
                                src: url('${require('@/assets/fonts/song.ttf')}') format('truetype');
                            }
                        `;

                        // 加载字体数据
                        const loadFontData = async (fontName) => {
                            if (process.env.IS_ELECTRON) {
                                // Electron 环境
                                const fs = window.require('fs').promises;
                                const path = window.require('path');

                                // 获取应用程序路径
                                const appPath = process.env.NODE_ENV === 'development'
                                    ? path.join(__dirname, `../src/assets/fonts/${fontName}.ttf`)
                                    : path.join(process.resourcesPath, `assets/fonts/${fontName}.ttf`);

                                try {
                                    const fontBuffer = await fs.readFile(appPath);
                                    return `data:font/ttf;base64,${fontBuffer.toString('base64')}`;
                                } catch (error) {
                                    console.error(`Error loading font ${fontName}:`, error);
                                    throw error;
                                }
                            } else {
                                // 浏览器环境
                                const response = await fetch(require(`@/assets/fonts/${fontName}.ttf`));
                                const blob = await response.blob();
                                return new Promise((resolve) => {
                                    const reader = new FileReader();
                                    reader.onloadend = () => resolve(reader.result);
                                    reader.readAsDataURL(blob);
                                });
                            }
                        };
                        try {
                            // 获取所有字体的 base64 数据
                            const [heiFont, kaiFont, songFont] = await Promise.all([
                                loadFontData('hei'),
                                loadFontData('kai'),
                                loadFontData('song')
                            ]);

                            // 修改 SVG 数据生成部分
                            const svgData = `
                                <svg xmlns="http://www.w3.org/2000/svg" 
                                    xmlns:xlink="http://www.w3.org/1999/xlink"
                                    width="${canvas.width / scale}" 
                                    height="${canvas.height / scale}"
                                    viewBox="${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}"
                                    shape-rendering="geometricPrecision"
                                    text-rendering="geometricPrecision">
                                    <defs>
                                        <style type="text/css">
                                            @font-face {
                                                font-family: 'hei';
                                                src: url('${heiFont}') format('truetype');
                                            }
                                            @font-face {
                                                font-family: 'kai';
                                                src: url('${kaiFont}') format('truetype');
                                            }
                                            @font-face {
                                                font-family: 'song';
                                                src: url('${songFont}') format('truetype');
                                            }
                                            text { 
                                                shape-rendering: geometricPrecision;
                                                text-rendering: geometricPrecision;
                                            }
                                        </style>
                                    </defs>
                                    <rect x="${viewBox.x}" y="${viewBox.y}" 
                                        width="${viewBox.width}" height="${viewBox.height}" 
                                        fill="white"/>
                                    ${svgElement.innerHTML.replace(
                                        /<text([^>]*?)x="50%"([^>]*?)>(.*?)<\/text>/g,
                                        (match, prefix, suffix, content) => {
                                            const centerX = viewBox.x + (viewBox.width / 2);
                                            return `<text${prefix}x="${centerX}"${suffix}>${content}</text>`;
                                        }
                                    )}
                                </svg>
                            `;

                            // ... 确保字体加载完成
                            await new Promise((resolve) => {
                                const tempDiv = document.createElement('div');
                                tempDiv.style.visibility = 'hidden';
                                tempDiv.style.fontFamily = 'hei, kai, song';
                                tempDiv.textContent = '字体加载测试';
                                document.body.appendChild(tempDiv);

                                // 使用 FontFaceObserver 或等待一小段时间确保字体加载
                                setTimeout(() => {
                                    document.body.removeChild(tempDiv);
                                    resolve();
                                }, 100);
                            });

                            // 将 SVG 转换为图片
                            await new Promise((resolve, reject) => {
                                const img = new Image();

                                img.onerror = (error) => {
                                    console.error('Image loading error:', error);
                                    reject(new Error('Failed to load SVG image'));
                                };

                                img.onload = () => {
                                    try {
                                        // 在高分辨率 canvas 上绘制图片
                                        ctx.drawImage(img, 0, 0, canvas.width / scale, canvas.height / scale);

                                        // 使用高质量的图片压缩
                                        const imgData = canvas.toDataURL('image/png', 1.0);

                                        // 添加到 PDF，使用原始尺寸
                                        pdf.addImage(imgData, 'PNG', 0, 0, A4_WIDTH_PT, A4_HEIGHT_PT, '', 'FAST');

                                        resolve();
                                    } catch (error) {
                                        reject(error);
                                    }
                                };

                                // 使用 base64 编码的 SVG 数据
                                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                            });

                            console.log(`Page ${pageIndex + 1} processed successfully`);
                        } catch (fontError) {
                            console.error('Error loading fonts:', fontError);
                            throw new Error('Failed to load fonts');
                        }

                    } catch (pageError) {
                        console.error(`Error processing page ${pageIndex + 1}:`, pageError);
                        throw pageError;
                    }
                }

                // 保存 PDF
                pdf.save('tablature.pdf');
                console.log('PDF saved successfully');

            } catch (error) {
                console.error('Error exporting PDF:', error);
                alert('PDF导出失败：' + (error.message || '未知错误'));
            } finally {
                this.isExporting = false;
            }
        }
    },
}
</script>

<style scoped>
.scrollable-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.canvas-svg {
    display: block;
    background: white;
}
</style>